import {
  EditorableSchema,
  FieldSchemaEditorSupportedTypes,
} from 'dashboard/components-lib/FieldSchemaEditor/constants';
import { Identifier } from 'dashboard/models/identifier';
import { FieldSchema, FieldSchemaType } from 'lib/fields';

export const hasNestedSchemas = (fieldSchema: EditorableSchema): boolean =>
  fieldSchema.type === FieldSchemaType.Hash ||
  (fieldSchema.type === FieldSchemaType.Array &&
    fieldSchema.config['item-schema'].type === FieldSchemaType.Hash);

const decorateFieldSchema = ({
  fieldSchema,
  identifiers = [],
  firstLoad = true,
  firstLayer = true,
}: {
  fieldSchema: FieldSchemaEditorSupportedTypes;
  identifiers: Identifier[];
  firstLoad: boolean;
  firstLayer: boolean;
}): EditorableSchema => {
  const identifier = firstLayer
    ? identifiers.find((identifier) => identifier.key === fieldSchema.key)
    : null;

  let schema;
  if (fieldSchema.type === FieldSchemaType.Hash) {
    schema = {
      ...fieldSchema,
      config: {
        ...fieldSchema.config,
        'item-schemas': fieldSchema.config['item-schemas'].map((itemSchema) => {
          return decorateFieldSchema({
            fieldSchema: itemSchema,
            identifiers,
            firstLoad,
            firstLayer: false,
          }) as unknown as FieldSchema;
        }),
      },
    };
  } else if (fieldSchema.type === FieldSchemaType.Array) {
    schema = {
      ...fieldSchema,
      config: {
        ...fieldSchema.config,
        'item-schema': decorateFieldSchema({
          fieldSchema: fieldSchema.config['item-schema'],
          identifiers,
          firstLoad,
          firstLayer: false,
        }) as unknown as FieldSchema,
      },
    };
  } else {
    schema = fieldSchema;
  }

  return {
    ...schema,
    ...(firstLoad ? { persisted: true } : {}),
    ...(firstLayer && identifier != null ? { identifier } : {}),
  };
};

/**
 * Decorate field schemas with additional properties that are necessary for form processing.
 * Does a preorder traversal of the tree to create a new decorated tree.
 */
export const decorateFieldSchemas = ({
  fieldSchemas,
  identifiers = [],
  firstLoad = true,
  firstLayer = true,
}: {
  fieldSchemas: FieldSchemaEditorSupportedTypes[];
  identifiers?: Identifier[];
  firstLoad?: boolean;
  firstLayer?: boolean;
}): EditorableSchema[] => {
  // - Any fieldSchema that comes through to defaultValues should have persisted: true
  // - Any fieldSchema that is also an identifier will get have identifier settings
  // added to the fieldSchema
  return fieldSchemas.map((fieldSchema) => {
    return decorateFieldSchema({
      fieldSchema,
      identifiers,
      firstLoad,
      firstLayer,
    });
  });
};

export const getDefaultValueDisplay = (
  defaultValue: string | boolean | undefined | string[],
): string => {
  if (defaultValue == null) {
    return '--';
  } else if (typeof defaultValue === 'boolean') {
    return defaultValue ? 'True' : 'False';
  } else if (Array.isArray(defaultValue)) {
    return defaultValue.map((value) => getDefaultValueDisplay(value)).join(', ');
  } else {
    return defaultValue;
  }
};
